import { createContext, useCallback, useEffect } from 'react'

import {
  type ChatGalleryResponse,
  ChatGalleryRestControllerService,
  OfChatMessagesService,
  type OnlyFansMessage,
  type OnlyFansMessageMedia,
} from '@api'
import { useUnit } from 'effector-react'
import * as _ from 'lodash'

import {
  addSentMedia,
  resetSentMedia,
  setFinishedPullingSentMedia,
  setMediaErrorMessage,
  setPostedMedia,
} from '@/models/chattingUI/media.ts'
import { $currentCreator } from '@/models/creators.ts'
import { sentMediaMessageService } from '@/modules/chatting/service/sent-media-message.ts'
import type {
  SentMediaMessage,
  SentMediaMessageMedia,
} from '@/modules/chatting/types.ts'
import { ChatMediaType } from '@/modules/chatting/types.ts'
import { waitRandom } from '@/utils/wait.ts'

const SentMediaMessageContext = createContext<null>(null)

export const SentMediaMessageProvider = ({
  children,
  fanId,
}: SentMediaMessageProviderProps) => {
  const creator = useUnit($currentCreator)
  // TODO IMPLEMENT THAT
  const abortCondition = useCallback(() => {
    return false
  }, [])

  useEffect(() => {
    resetSentMedia()
    setMediaErrorMessage(null)
    setFinishedPullingSentMedia(false)

    if (!fanId || fanId <= 0 || !creator?.id || !creator?.onlyfansId) {
      return
    }

    retrieveSentMedia(creator.id, creator.onlyfansId, fanId, abortCondition)
  }, [creator?.id, creator?.onlyfansId, fanId, abortCondition])

  return (
    <SentMediaMessageContext.Provider value={null}>
      {children}
    </SentMediaMessageContext.Provider>
  )
}

export interface SentMediaMessageProviderProps {
  children: React.ReactNode
  fanId: number
}

const retrieveSentMedia = async (
  creatorId: number,
  onlyfansCreatorId: number,
  fanId: number,
  abortCondition: () => boolean,
) => {
  // Getting cached sent media messages from server
  const sentMediaMessageServerResponse =
    await sentMediaMessageService.getSentMediaMessages(creatorId, fanId)
  let sentMediaMessages = _.reverse(
    sentMediaMessageServerResponse.sentMediaMessages,
  )

  setPostedMedia(sentMediaMessageServerResponse.postedMedia)
  addSentMedia(sentMediaMessages)

  // Changing order from oldest to newest
  try {
    // Getting media messages from gallery first
    const newGalleryMediaMessages = _.reverse(
      await getGalleryMessages(
        creatorId,
        onlyfansCreatorId,
        fanId,
        sentMediaMessages,
        abortCondition,
      ),
    ).filter(
      (message) => !sentMediaMessages.some(({ id }) => id === message.id),
    )

    sentMediaMessages = _.uniqBy(
      [...sentMediaMessages, ...newGalleryMediaMessages],
      (element) => element.id,
    )

    if (abortCondition()) {
      throw new Error('Aborted')
    }

    // Getting potential media messages which are not in the gallery
    const newMediaMessages = _.reverse(
      (
        await OfChatMessagesService.getRecentMediaMessages({
          creatorId,
          userId: fanId,
          since:
            sentMediaMessages.length > 0
              ? sentMediaMessages[sentMediaMessages.length - 1].createdAt
              : new Date(
                  new Date().getTime() - 24 * 60 * 60 * 1000,
                ).toISOString(),
        })
      ).map((message) => messageToMediaMessage(fanId, message)),
    ).filter(
      (message) =>
        !sentMediaMessages.find((stored) => stored.id === message.id),
    )

    sentMediaMessages = _.uniqBy(
      [...sentMediaMessages, ...newMediaMessages],
      (element) => element.id,
    )
    sentMediaMessages = sentMediaMessages.sort(
      (a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt),
    )

    addSentMedia(newMediaMessages)
    setFinishedPullingSentMedia(true)

    if (sentMediaMessages.length > 0) {
      sentMediaMessages[0].first = true
    }

    // Pushing all media messages that have not yet been stored on the server
    const mediaMessagesToPush = [...sentMediaMessages].filter(
      (storedMessage) =>
        !sentMediaMessageServerResponse ||
        !sentMediaMessageServerResponse.sentMediaMessages.find(
          (serverMessage) => serverMessage.id === storedMessage.id,
        ),
    )

    if (mediaMessagesToPush.length > 0) {
      pushMediaMessagesToApi(creatorId, fanId, mediaMessagesToPush)
    }
  } catch (error) {
    if (error instanceof Error && error?.message === 'Aborted') {
      return null
    }

    setMediaErrorMessage(error instanceof Error ? error.message : String(error))
    console.error(`Error while loading sent media for fan ${fanId}`, error)
  }
}

const pushMediaMessagesToApi = async (
  creatorId: number,
  fanId: number,
  messages: SentMediaMessage[],
) => {
  const mediaChunks = _.chunk(messages, 20)

  for (const chunk of mediaChunks) {
    try {
      await sentMediaMessageService.postMediaMessages(creatorId, fanId, chunk)
    } catch (error) {
      console.error('Error while pushing sent media messages', error)

      return false
    }
  }

  return true
}

const getGalleryMessages = async (
  creatorId: number,
  onlyfansCreatorId: number,
  fanId: number,
  storedMediaMessages: SentMediaMessage[],
  abortCondition: () => boolean,
): Promise<SentMediaMessage[]> => {
  let mediaGetResult: ChatGalleryResponse | null = null
  const galleryMessages: SentMediaMessage[] = []
  let lastMediaId = null

  let finished = false

  do {
    if (abortCondition()) {
      throw new Error('Aborted')
    }

    mediaGetResult = await ChatGalleryRestControllerService.getGallery({
      creatorId,
      fanId,
      type: ChatMediaType.All,
      lastId: lastMediaId!,
      mapCdnUrls: true,
    })
    const pageMessages: SentMediaMessage[] = []

    if (mediaGetResult.list) {
      for (const messageEntry of mediaGetResult.list) {
        if (String(messageEntry.fromUser!.id) !== String(onlyfansCreatorId)) {
          continue
        }

        if (
          storedMediaMessages.find(
            (storedMessage) => storedMessage.id === messageEntry.id,
          )
        ) {
          finished = true
          break
        }

        const mappedMessage = messageToMediaMessage(fanId, messageEntry)

        galleryMessages.push(mappedMessage)
        pageMessages.push(mappedMessage)
      }

      if (pageMessages.length > 0) {
        addSentMedia(pageMessages)
      }

      if (finished) {
        return galleryMessages
      }
    }

    if (mediaGetResult.hasMore && mediaGetResult.nextLastId) {
      lastMediaId = +mediaGetResult.nextLastId
      await waitRandom(1000, 2000)
    } else {
      finished = true
      break
    }
  } while (!finished)

  return galleryMessages
}

export const messageToMediaMessage = (
  fanId: number,
  message: OnlyFansMessage,
): SentMediaMessage => {
  return {
    id: message.id!,
    fanId: fanId,
    createdAt: message.createdAt!,
    isOpened: message.isOpened!,
    isFree: message.isFree!,
    price: message.price!,
    text: message.text!,
    purchaseDate: message.purchaseDate!,
    canPurchaseReason: 'dummy',
    sentBy: message.fromUser!.id === fanId ? 'fan' : 'creator',
    first: false,
    media: message.media?.map(mediaToSentMediaMessageMedia) || [],
  }
}

const mediaToSentMediaMessageMedia = (
  messageMedia: OnlyFansMessageMedia,
): SentMediaMessageMedia => ({
  id: messageMedia.id!,
  type: messageMedia.type!,
  // @ts-ignore
  files: messageMedia.files,
  duration: messageMedia.duration!,
})
